
import { format, formatDistanceToNow, differenceInDays, differenceInCalendarDays, parseISO, isValid } from 'date-fns';
import { useAuthStore } from '@/store/authStore';

// === CURRENCY ===

export function formatCurrency(amount: number | string | null | undefined, currency = 'USD'): string {
  const n = Number(amount);
  if (amount === null || amount === undefined || isNaN(n)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatCurrencyCompact(amount: number | string | null | undefined): string {
  const n = Number(amount);
  if (amount === null || amount === undefined || isNaN(n)) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return formatCurrency(n);
}

// === NUMBERS ===

export function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined) return '0';
  return new Intl.NumberFormat('en-US').format(num);
}

export function formatNumberCompact(num: number | null | undefined): string {
  if (num === null || num === undefined) return '0';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return String(num);
}

export function formatPercentage(num: number | null | undefined): string {
  if (num === null || num === undefined) return '0%';
  return `${num.toFixed(1)}%`;
}

export function formatHourlyRate(value: number | null | undefined, hours: number | null | undefined): string {
  if (!value || !hours || hours === 0) return '—';
  return formatCurrency(value / hours) + '/hr';
}

// === DATES ===

// Timezone resolution for user-facing absolute dates.
//
// A creator can configure profile.timezone (ProfileSettings), but until now no
// display helper read it: every timestamp rendered in the browser's local zone,
// so a creator in UTC+9 saw deadline/billing timestamps in whatever zone their
// machine happened to be set to — diverging from the UTC boundaries the backend
// enforces. Centralizing the lookup here means every call site that uses these
// helpers renders in the creator's configured zone without threading the value
// through each component. An explicit `timeZone` argument always wins, which
// keeps the helpers unit-testable without standing up the store.
function resolveTimeZone(timeZone?: string): string | undefined {
  return (timeZone ?? useAuthStore.getState().profile?.timezone) || undefined;
}

// A bare date-only string ('2025-04-01') is a calendar date, not an instant: it
// carries no time-of-day and therefore no timezone. Rendering it in a different
// zone would drift it across midnight by up to a day (the exact bug the helpers
// below already guard against for parsing). Only inputs carrying a time
// component are true instants whose displayed wall-clock date depends on the
// viewer's timezone, so timezone application is gated on this.
function hasTimeComponent(date: string): boolean {
  return date.includes('T');
}

// Render `parsed` in `tz` with the given Intl options, falling back to the
// already-computed browser-local string when no zone is configured or the
// configured id is not a valid IANA zone (profile.timezone is user-settable).
function renderInZone(
  parsed: Date,
  tz: string | undefined,
  options: Intl.DateTimeFormatOptions,
  fallback: string,
): string {
  if (!tz) return fallback;
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, ...options }).format(parsed);
  } catch {
    return fallback;
  }
}

export function formatDate(date: string | null | undefined, timeZone?: string): string {
  if (!date) return '—';
  const parsed = parseISO(date);
  if (!isValid(parsed)) return '—';
  const local = format(parsed, 'MMM d, yyyy');
  const tz = hasTimeComponent(date) ? resolveTimeZone(timeZone) : undefined;
  return renderInZone(parsed, tz, { month: 'short', day: 'numeric', year: 'numeric' }, local);
}

export function formatDateShort(date: string | null | undefined, timeZone?: string): string {
  if (!date) return '—';
  const parsed = parseISO(date);
  if (!isValid(parsed)) return '—';
  const local = format(parsed, 'MMM d');
  const tz = hasTimeComponent(date) ? resolveTimeZone(timeZone) : undefined;
  return renderInZone(parsed, tz, { month: 'short', day: 'numeric' }, local);
}

export function formatDateRelative(date: string | null | undefined): string {
  if (!date) return '—';
  const parsed = parseISO(date);
  if (!isValid(parsed)) return '—';
  return formatDistanceToNow(parsed, { addSuffix: true });
}

export function getDaysUntil(date: string | null | undefined): number | null {
  if (!date) return null;
  const parsed = parseISO(date);
  if (!isValid(parsed)) return null;
  return differenceInDays(parsed, new Date());
}

export function getDaysOverdue(dueDate: string | null | undefined): number {
  if (!dueDate) return 0;
  const parsed = parseISO(dueDate);
  if (!isValid(parsed)) return 0;
  const diff = differenceInDays(new Date(), parsed);
  return Math.max(0, diff);
}

// Boolean-returning date helpers. Use these in place of inline
// `new Date(api_date) < new Date()` comparisons — the inline form parses
// date-only strings as UTC midnight while comparing to browser-local now,
// drifting by up to 24h for non-UTC users. parseISO + differenceInCalendarDays
// keeps both operands in the user's local frame.
//
// Convention: null / undefined / invalid input → false. A missing date
// doesn't make something overdue, and treating malformed input as
// "not late" is the safer default for trust-sensitive UI.

export function isPastDate(date: string | null | undefined): boolean {
  if (!date) return false;
  const parsed = parseISO(date);
  if (!isValid(parsed)) return false;
  return differenceInCalendarDays(new Date(), parsed) > 0;
}

export function isWithinDays(date: string | null | undefined, days: number): boolean {
  if (!date) return false;
  if (days < 0) return false;
  const parsed = parseISO(date);
  if (!isValid(parsed)) return false;
  const diff = differenceInCalendarDays(parsed, new Date());
  return diff >= 0 && diff <= days;
}

export function isOverdue(dueDate: string | null | undefined, paid: boolean = false): boolean {
  if (paid) return false;
  return isPastDate(dueDate);
}

// TZ-coherent quarter attribution. parseISO keeps the parse and getMonth() in
// the same local frame; new Date(dateStr).getMonth() reads date-only strings
// as UTC midnight and drifts by up to 24h in any browser west of UTC. A paid
// date of '2025-04-01' would otherwise land in Q1 instead of Q2 in HST/PST.
export function getQuarter(date: string | null | undefined): string {
  if (!date) return '—';
  const parsed = parseISO(date);
  if (!isValid(parsed)) return '—';
  return `Q${Math.ceil((parsed.getMonth() + 1) / 3)}`;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

// === INVOICE ===

export function formatInvoiceNumber(num: number): string {
  return `CROS-${String(num).padStart(4, '0')}`;
}

